import { FIXED_SESSION_TYPE } from '../shared/utils/constants';

const ZERO_VALUE = '0.00';
const FORMAT_CATEGORY_SEPARATOR = '|*|';

export const calculatePrice = (
    minuteDuration,
    categoryId,
    rentalTypes = [],
    transferHistory,
    categoriesMap
) => {
    if (!transferHistory?.length) {
        return calculateCurrentSessionPrice(
            minuteDuration,
            categoriesMap[categoryId].hourlyRate,
            categoriesMap[categoryId].customRates,
            rentalTypes
        );
    }

    return calculatePriceIncludingTransferHistory(
        minuteDuration,
        categoryId,
        rentalTypes,
        transferHistory,
        categoriesMap
    );
};

export const calculateMenuPrice = (usedProducts) => {
    return usedProducts.reduce(
        (partialSum, product) =>
            (
                parseFloat(partialSum) +
                product.quantity * parseFloat(product.price.$numberDecimal)
            ).toFixed(2),
        0
    );
};

export const getIgnoreCustomRates = (rentalTypes = []) => {
    return rentalTypes.some((type) => type.ignoreCustomRates);
};

const formatCategoryId = (categoryId, shouldIgnoreCustomRates = false) => {
    return `${categoryId}${FORMAT_CATEGORY_SEPARATOR}${!!shouldIgnoreCustomRates}`;
};

export const calculatePriceIncludingTransferHistory = (
    minuteDuration,
    categoryId,
    rentalTypes = [],
    transferHistory = [],
    categoriesMap
) => {
    const shouldIgnoreCustomRatesForCurrentCategory = getIgnoreCustomRates(rentalTypes);

    const msDurationPerFormatedCategory = {
        [formatCategoryId(categoryId, shouldIgnoreCustomRatesForCurrentCategory)]:
            minuteDuration * 60 * 1000,
    };
    const rentalTypesPricePerFormatedCategory = {
        [formatCategoryId(categoryId, shouldIgnoreCustomRatesForCurrentCategory)]:
            calculatePriceForRentalTypes(minuteDuration, rentalTypes),
    };

    for (let transfer of transferHistory) {
        const shouldIgnoreCustomRates = getIgnoreCustomRates(transfer.rentalTypes);
        const formattedCategoryId = formatCategoryId(
            transfer.rentalCategoryId,
            shouldIgnoreCustomRates
        );

        if (!msDurationPerFormatedCategory[formattedCategoryId]) {
            msDurationPerFormatedCategory[formattedCategoryId] = 0;
            rentalTypesPricePerFormatedCategory[formattedCategoryId] = 0;
        }

        const segmentMSDuration = calculateSessionMSDuration(
            transfer.startTimestamp,
            transfer.endTimestamp,
            transfer.pauses
        );

        msDurationPerFormatedCategory[formattedCategoryId] += segmentMSDuration;

        rentalTypesPricePerFormatedCategory[formattedCategoryId] += calculatePriceForRentalTypes(
            msToMinutes(segmentMSDuration),
            transfer.rentalTypes
        );
    }

    let totalPrice = 0.0;

    for (let curFormattedCategoryId of Object.keys(msDurationPerFormatedCategory)) {
        const [curCategoryId, curShouldIgnoreCustomRatesString] =
            curFormattedCategoryId.split(FORMAT_CATEGORY_SEPARATOR);
        const curShouldIgnoreCustomRates = curShouldIgnoreCustomRatesString === 'true';
        const curCategory = categoriesMap[curCategoryId];
        let categoryPrice = rentalTypesPricePerFormatedCategory[curFormattedCategoryId];

        if (!curCategory) {
            console.error('No category found for categoryId:', curCategoryId);
            continue;
        }

        categoryPrice += calculatePriceForCustomRates(
            msToMinutes(msDurationPerFormatedCategory[curFormattedCategoryId]),
            curCategory.hourlyRate,
            curShouldIgnoreCustomRates ? [] : curCategory.customRates
        );

        totalPrice += categoryPrice > 0 ? categoryPrice : 0;
    }

    return totalPrice > 0 ? totalPrice.toFixed(2) : ZERO_VALUE;
};

export const calculateCurrentSessionPrice = (
    minuteDuration = 0,
    hourlyRate,
    customRates = [],
    rentalTypes = []
) => {
    const shouldIgnoreCustomRates = getIgnoreCustomRates(rentalTypes);

    let totalPrice =
        calculatePriceForCustomRates(
            minuteDuration,
            hourlyRate,
            shouldIgnoreCustomRates ? [] : customRates
        ) + calculatePriceForRentalTypes(minuteDuration, rentalTypes);

    return totalPrice > 0 ? totalPrice.toFixed(2) : ZERO_VALUE;
};

export const calculateMinuteDurationForPrice = (
    price = 0,
    hourlyRate,
    customRates = [],
    rentalTypes = []
) => {
    if (!hourlyRate.$numberDecimal) {
        hourlyRate = { $numberDecimal: parseFloat(hourlyRate) };
    }

    const rentalsRate = rentalTypes.reduce(
        (curSum, type) => curSum + parseFloat(type.hourlyRateIncrease.$numberDecimal),
        0.0
    );

    const sortedCustomRates = [
        ...(getIgnoreCustomRates(rentalTypes) ? [] : customRates),
        { timeUnit: 60, rate: { ...hourlyRate } },
    ]
        .map((customRate) => ({
            timeUnit: customRate.timeUnit,
            rate: {
                $numberDecimal:
                    parseFloat(customRate.rate.$numberDecimal) +
                    (customRate.timeUnit / 60) * rentalsRate,
            },
        }))
        .sort((a, b) => {
            return b.timeUnit - a.timeUnit;
        });

    let curPrice = price;
    let totalDuration = 0;

    sortedCustomRates.forEach((customRate) => {
        totalDuration +=
            Math.floor(curPrice / parseFloat(customRate.rate.$numberDecimal)) * customRate.timeUnit;

        curPrice %= parseFloat(customRate.rate.$numberDecimal);
    });

    if (curPrice > 0) {
        const smallestCustomRate = sortedCustomRates[sortedCustomRates.length - 1];

        totalDuration += Math.floor(
            (curPrice / (parseFloat(smallestCustomRate.rate.$numberDecimal) + rentalsRate)) *
                smallestCustomRate.timeUnit
        );
    }

    if (!totalDuration || totalDuration < 0) {
        totalDuration = 0;
    }

    return totalDuration;
};

export const calculateHourlyRate = (hourlyRate, rentalTypes = []) => {
    if (!hourlyRate.$numberDecimal) {
        hourlyRate = { $numberDecimal: parseFloat(hourlyRate) };
    }

    let curHourlyRate = rentalTypes.reduce(
        (curSum, type) => curSum + parseFloat(type.hourlyRateIncrease.$numberDecimal),
        parseFloat(hourlyRate.$numberDecimal)
    );

    if (curHourlyRate < 0) {
        curHourlyRate = 0.0;
    }

    return curHourlyRate.toFixed(2);
};

export const formatTime = (time) => {
    return time < 10 ? '0' + time : time;
};

export const calculateOffset = (startTimestamp) => {
    const curTimestamp = Date.now();
    const timePassed = curTimestamp - startTimestamp;
    return new Date(curTimestamp + timePassed);
};

export const roundValue = (value, orgOptions) => {
    value = parseFloat(value);
    if (orgOptions && orgOptions.rounding) {
        const roundingPoint = orgOptions.rounding.point
            ? parseFloat(orgOptions.rounding.point.$numberDecimal)
            : 0.5;

        switch (orgOptions.rounding.mode) {
            case 'UP':
                return Math.ceil(value / roundingPoint) * roundingPoint;

            case 'DOWN':
                return Math.floor(value / roundingPoint) * roundingPoint;

            case 'ROUND':
            default:
                return Math.round(value / roundingPoint) * roundingPoint;
        }
    }

    return value;
};

export const calculateSessionMSDuration = (startTimestamp, endTimestamp, pauses) => {
    return (
        parseInt(endTimestamp) -
        parseInt(startTimestamp) -
        calculatePauseDuration(endTimestamp, pauses)
    );
};

export const calculatePauseDuration = (endTimestamp, pauses) => {
    let duration = 0;

    for (let pause of pauses) {
        if (!pause.endTimestamp) {
            duration += parseInt(endTimestamp) - parseInt(pause.startTimestamp);
            break;
        } else {
            duration += parseInt(pause.endTimestamp) - parseInt(pause.startTimestamp);
        }
    }

    return duration;
};

export const calculateMsDurationInCategoryFromTransferHistory = (transferHistory, categoryId) => {
    let msDuration = 0;

    for (let transfer of transferHistory) {
        if (transfer.rentalCategoryId !== categoryId) {
            continue;
        }

        msDuration += calculateSessionMSDuration(
            transfer.startTimestamp,
            transfer.endTimestamp,
            transfer.pauses
        );
    }

    return msDuration;
};

export const msToMinutes = (ms) => {
    return Math.floor(ms / 1000) / 60;
};

export const msToSeconds = (ms) => {
    return Math.floor(ms / 1000);
};

export const isFixedType = (sessionType) => {
    return sessionType.toUpperCase() === FIXED_SESSION_TYPE;
};

const calculatePriceForRentalTypes = (minuteDuration, rentalTypes = []) => {
    return (
        (minuteDuration / 60) *
        rentalTypes.reduce(
            (curSum, type) => curSum + parseFloat(type.hourlyRateIncrease.$numberDecimal),
            0.0
        )
    );
};

const calculatePriceForCustomRates = (minuteDuration, hourlyRate, customRates = []) => {
    if (!hourlyRate.$numberDecimal) {
        hourlyRate = { $numberDecimal: parseFloat(hourlyRate) };
    }

    let totalPrice = 0.0;
    let curMinuteDuration = minuteDuration;

    const sortedCustomRates = [...customRates, { timeUnit: 60, rate: { ...hourlyRate } }].sort(
        (a, b) => {
            return b.timeUnit - a.timeUnit;
        }
    );

    sortedCustomRates.forEach((customRate) => {
        totalPrice +=
            Math.floor(curMinuteDuration / customRate.timeUnit) *
            parseFloat(customRate.rate.$numberDecimal);

        curMinuteDuration %= customRate.timeUnit;
    });

    if (curMinuteDuration > 0) {
        const smallestCustomRate = sortedCustomRates[sortedCustomRates.length - 1];

        totalPrice +=
            (parseFloat(smallestCustomRate.rate.$numberDecimal) * curMinuteDuration) /
            smallestCustomRate.timeUnit;

        const alternatePrice = parseFloat(
            calculatePriceForCustomRates(
                minuteDuration + (smallestCustomRate.timeUnit - curMinuteDuration),
                hourlyRate,
                customRates
            )
        );

        totalPrice = Math.min(totalPrice, alternatePrice);
    }

    return totalPrice;
};

